/* eslint-disable @khan/frontend/dont-hardcode-cdn -- this is allowed in this file */
/**
 * Tools for returning paths to static content given a path url.
 */
const URL_BASE_REGEX = /^(?:https?:)?\/\/([^/]*)\//;
// sync-start:dev-domains 1756876871 https://github.com/Khan/webapp/blob/master/dev/server/Makefile
const KA_DOMAIN_REGEX =
    /(^|\.)(khanacademy\.(org|dev)|kastatic\.org|(.*-dot-)?khan-academy\.appspot\.com)/;
// sync-end:dev-domains

// sync-start:fastly-url-map 1005434587 https://github.com/Khan/webapp/blob/master/services/content-editing/models/content_validation.go
// sync-start:fastly-url-map 1005434587 https://github.com/Khan/webapp/blob/master/services/content-editing/publish/models/urls.go
// sync-start:fastly-url-map 1005434587 https://github.com/Khan/webapp/blob/master/services/content/getters/url_constants.go

const PROXIED_S3_BUCKETS = [
    "KA-youtube-converted",
    "KA-share",
    "ka-article-iframes",
    "ka-cs-algorithms",
    "ka-cs-challenge-images",
    "ka-cs-scratchpad-audio",
    "ka-exercise-screenshots",
    "ka-exercise-screenshots-2",
    "ka-exercise-screenshots-3",
    "ka-learnstorm",
    "ka-mobile",
    "ka-perseus-graphie",
    "ka-perseus-images",
    "ka-team-images",
];
const PROXIED_GCS_BUCKETS = [
    "ka_thumbnails_cache",
    "ka-youtube-converted",
    "ka-cs-audio",
    "tap-data",
];
const FASTLY_URL_MAP = [
    ["lh3.googleusercontent.com", "cdn.kastatic.org/googleusercontent"],
].concat(
    PROXIED_S3_BUCKETS.map((b) => [
        `${b}.s3.amazonaws.com`,
        `cdn.kastatic.org/${b}`,
    ]),
    PROXIED_S3_BUCKETS.map((b) => [
        `s3.amazonaws.com/${b}`,
        `cdn.kastatic.org/${b}`,
    ]),
    PROXIED_GCS_BUCKETS.map((b) => [
        `${b}.storage.googleapis.com`,
        `cdn.kastatic.org/${b}`,
    ]),
    PROXIED_GCS_BUCKETS.map((b) => [
        `storage.googleapis.com/${b}`,
        `cdn.kastatic.org/${b}`,
    ]),
);
// sync-end:fastly-url-map

interface StaticUrlFn {
    (maybeRelativeUrl: string): string;
    (maybeRelativeUrl?: null | undefined): null | undefined;
}

const staticProtocol = "https:";
const staticHostname = "cdn.kastatic.org";
const staticUrlBase: string = `${staticProtocol}//${staticHostname}`;

// return a path to this static content given a path url - example:
// staticUrl('/mathjax.js') -> 'https://www.kastatic.org/mathjax.js'
export const staticUrl: StaticUrlFn = (maybeRelativeUrl) => {
    if (!maybeRelativeUrl) {
        // We define the interface such that TypeScript can infer calls properly.
        // However, it means that return types are hard to match here in
        // the implementation.
        return maybeRelativeUrl;
    }

    if (maybeRelativeUrl.startsWith(".")) {
        throw new Error("Static URL cannot be relative.");
    }

    // If the URL doesn't have a domain, we need to add the static URL base.
    if (!URL_BASE_REGEX.test(maybeRelativeUrl)) {
        const updatedUrl = new URL(maybeRelativeUrl, staticUrlBase);
        return updatedUrl.toString();
    }

    let updatedUrl = maybeRelativeUrl.startsWith("//")
        ? `https:${maybeRelativeUrl}`
        : maybeRelativeUrl;

    for (const [subst, repl] of FASTLY_URL_MAP) {
        updatedUrl = updatedUrl.replace(subst, repl);
    }

    // Check domain
    const url = new URL(updatedUrl);

    // If the URL is a KA domain, rewrite it to the static hostname and protocol
    if (url.hostname.match(KA_DOMAIN_REGEX)) {
        url.protocol = staticProtocol;
        url.hostname = staticHostname;
    }

    return url.toString();
};

// return text suitable for a CSS 'url()' element - example:
// cssUrl('/image.jpg') -> 'url(https://www.kastatic.org/image.jpg)'
export function cssUrl(maybeRelativeUrl: string): string {
    return "url(" + staticUrl(maybeRelativeUrl) + ")";
}
